import { useEffect, useState } from 'react';
import { callFn } from '../lib/api';
import type { Reservation } from '../lib/types';
import { reservationStatusLabel } from '../lib/types';
import { useApp } from '../app/AppContext';
import { DataTable, type Column } from '../components/DataTable';

export function MyReservations() {
  const { toast, refreshReadyCount } = useApp();
  const [rows, setRows] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const r = await callFn<Reservation[]>('list_my_reservations');
      setRows(r);
      void refreshReadyCount(); // keep the nav badge in sync
    }
    catch (e) { toast(e instanceof Error ? e.message : 'Failed to load', 'err'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, []);

  async function cancel(id: string) {
    if (!confirm('Cancel this reservation? The item goes back into the pool.')) return;
    try { await callFn('cancel_reservation', { id }); toast('Reservation cancelled', 'ok'); void load(); }
    catch (e) { toast(e instanceof Error ? e.message : 'Could not cancel', 'err'); }
  }

  async function markPickedUp(id: string) {
    try { await callFn('foster_mark_pickup', { id }); toast('Marked picked up. Thank you!', 'ok'); void load(); }
    catch (e) { toast(e instanceof Error ? e.message : 'Could not mark picked up', 'err'); }
  }

  const cols: Column<Reservation>[] = [
    {
      key: 'item', label: 'Item', get: (r) => r.item_description || '',
      render: (r) => <>{r.item_description}{r.category_name ? <span className="badge cat" style={{ marginLeft: 6 }}>{r.category_name}</span> : null}</>,
    },
    { key: 'qty', label: 'Qty', get: (r) => r.quantity, numeric: true, align: 'right' },
    {
      key: 'pickup', label: 'Pickup', get: (r) => r.location_name || '—',
      render: (r) => <>{r.location_name || '—'}{r.location_phone ? <div className="page-sub" style={{ margin: 0, fontSize: '.78rem' }}>{r.location_phone}</div> : null}</>,
    },
    { key: 'status', label: 'Status', get: (r) => reservationStatusLabel(r.status), render: (r) => <span className={`badge ${r.status}`}>{reservationStatusLabel(r.status)}</span> },
    { key: 'reserved', label: 'Reserved', get: (r) => r.reserved_at, render: (r) => new Date(r.reserved_at).toLocaleDateString() },
  ];

  if (loading) return <div className="loading">Loading…</div>;

  return (
    <>
      <h1 className="page-title">My reservations</h1>
      <p className="page-sub">What you&rsquo;ve reserved and where to pick it up.</p>
      <DataTable
        columns={cols}
        rows={rows}
        rowKey={(r) => r.id}
        exportName="my-reservations"
        empty="You haven't reserved anything yet."
        actions={(r) => (
          <>
            {r.status === 'ready' && <button className="btn small" onClick={() => markPickedUp(r.id)}>Mark picked up</button>}
            {/* Fosters can cancel only while 'reserved'; once an admin marks it ready, only an admin can cancel. */}
            {r.status === 'reserved' && <button className="btn ghost small" onClick={() => cancel(r.id)}>Cancel</button>}
          </>
        )}
      />
    </>
  );
}
